import React from 'react';
import { Table, Button, Spin, Empty } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { CustomOffer } from '../types';
import { message } from 'antd';

interface CustomOffersTableProps {
	offers: CustomOffer[];
	isLoading: boolean;
}

const CustomOffersTable: React.FC<CustomOffersTableProps> = ({ offers, isLoading }) => {
	const formatPrice = (price: number): string => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(price);
	};

	const copyToClipboard = (text: string): void => {
		navigator.clipboard.writeText(text);
		message.success('Copied to clipboard!');
	};

	const handleCopyUrl = (url: string): void => {
		copyToClipboard(url);
	};

	const renderId = (id: string): JSX.Element => {
		return <span className="font-medium">{id.slice(0, 8)}...</span>;
	};

	const renderType = (quantity: number): string => {
		return quantity === 1 ? 'Unlimited' : 'Per Seat';
	};

	const renderQuantity = (quantity: number): number | string => {
		return quantity === 1 ? 'Unlimited' : quantity;
	};

	const renderPrice = (price: number): string => {
		return formatPrice(price);
	};

	const renderBilling = (_: any, record: CustomOffer): JSX.Element => {
		return (
			<span>
				Every {record.intervalCount} {record.interval}
				{record.intervalCount > 1 ? 's' : ''}
			</span>
		);
	};

	const renderTrial = (trialPeriodDays: number): string => {
		return trialPeriodDays ? `${trialPeriodDays} days` : 'No trial';
	};

	const renderCardRequired = (cardRequired: boolean): string => {
		return cardRequired ? 'Required' : 'Optional';
	};

	const renderUrl = (url: string): JSX.Element => {
		return (
			<div className="max-w-xs truncate" title={url}>
				{url}
			</div>
		);
	};

	const renderActions = (_: any, record: CustomOffer): JSX.Element => {
		return (
			<Button
				type="link"
				icon={<CopyOutlined />}
				onClick={() => handleCopyUrl(record.URL)}
				className="text-indigo-600 hover:text-indigo-900 p-0 h-auto"
			>
				Copy URL
			</Button>
		);
	};

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			render: renderId,
		},
		{
			title: 'Type',
			dataIndex: 'quantity',
			key: 'type',
			render: renderType,
		},
		{
			title: 'Quantity',
			dataIndex: 'quantity',
			key: 'quantity',
			render: renderQuantity,
		},
		{
			title: 'Price',
			dataIndex: 'priceQuote',
			key: 'price',
			render: renderPrice,
		},
		{
			title: 'Billing',
			key: 'billing',
			render: renderBilling,
		},
		{
			title: 'Trial',
			dataIndex: 'trialPeriodDays',
			key: 'trial',
			render: renderTrial,
		},
		{
			title: 'Card Required',
			dataIndex: 'cardRequired',
			key: 'cardRequired',
			render: renderCardRequired,
		},
		{
			title: 'URL',
			dataIndex: 'URL',
			key: 'url',
			render: renderUrl,
		},
		{
			title: 'Actions',
			key: 'actions',
			render: renderActions,
		},
	];

	if (isLoading) {
		return (
			<div className="text-center py-8">
				<Spin size="large" />
				<div className="mt-4">Loading offers...</div>
			</div>
		);
	}

	if (offers.length === 0) {
		return (
			<div className="text-center py-8">
				<Empty description="No custom offers found." />
			</div>
		);
	}

	return (
		<Table
			columns={columns}
			dataSource={offers}
			rowKey="id"
			pagination={false}
			className="overflow-x-auto"
			rowClassName="hover:bg-gray-50 transition-colors"
		/>
	);
};

export default CustomOffersTable;
